import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Flex,
  Select,
  TagBadge,
  Text,
  Tooltip,
  TooltipTrigger,
  useToast,
} from '@chatsdet/ui';
import { Robot, Warning } from '@phosphor-icons/react';
import {
  useUpdateOrgAiConfigMutation,
  type AgentRole,
  type ModelTier,
  type OrganizationWithStats,
  type ProviderName,
  type ProviderResolution,
} from './useSuperadminData';
import { modelOptionsForProvider, useProviderModelOptions } from './useAiModelCatalog';

const PROVIDER_SOURCE_COLOR: Record<ProviderResolution['source'], 'blue' | 'purple' | 'gray'> = {
  org: 'blue',
  'knob-default': 'purple',
  default: 'gray',
};

const MODEL_TIERS: readonly ModelTier[] = ['main', 'chat', 'small', 'nano', 'pageTester'];

const MODEL_TIER_LABEL: Record<ModelTier, string> = {
  main: 'Main',
  chat: 'Chat',
  small: 'Small',
  nano: 'Nano',
  pageTester: 'Page tester',
};

type AgentSelection = { provider: ProviderName | ''; model: string };
type AgentSelections = Partial<Record<AgentRole, AgentSelection>>;

const AGENT_ROW_GRID =
  'grid grid-cols-[minmax(8rem,11rem)_minmax(0,1fr)_minmax(0,1fr)] items-center gap-x-3';

const EMPTY_AGENT_SELECTION: AgentSelection = { provider: '', model: '' };

const EMPTY_MODEL_SELECTIONS: Record<ModelTier, string> = {
  main: '',
  chat: '',
  small: '',
  nano: '',
  pageTester: '',
};

const providerSourceTooltip = (resolution: ProviderResolution): string => {
  const tiers = MODEL_TIERS.map(
    (tier) => `${MODEL_TIER_LABEL[tier]}: ${resolution.models?.[tier] ?? '(default)'}`
  ).join('\n');
  switch (resolution.source) {
    case 'org':
      return `Set on the org\n${tiers}`;
    case 'knob-default':
      return `From the ai.provider.default knob\n${tiers}`;
    case 'default':
      return `BASE_PROVIDER default (no org provider, no knob)\n${tiers}`;
  }
};

export function tierModelSelectionsFromConfig(
  models: Partial<Record<ModelTier, string | null>> | undefined
): Record<ModelTier, string> {
  return {
    main: models?.main ?? '',
    chat: models?.chat ?? '',
    small: models?.small ?? '',
    nano: models?.nano ?? '',
    pageTester: models?.pageTester ?? '',
  };
}

function agentSelectionsFromConfig(
  agents:
    | Partial<Record<AgentRole, { provider?: ProviderName | null; model?: string | null }>>
    | undefined
): AgentSelections {
  return Object.fromEntries(
    Object.entries(agents ?? {}).map(([role, selection]) => [
      role,
      { provider: selection?.provider ?? '', model: selection?.model ?? '' },
    ])
  ) as AgentSelections;
}

function filterAgentModelSelectionForProvider(
  previous: AgentSelection,
  allowedModels: readonly string[]
): AgentSelection {
  return {
    ...previous,
    model: previous.model && !allowedModels.includes(previous.model) ? '' : previous.model,
  };
}

function filterInheritedAgentModelsForProvider(
  previous: AgentSelections,
  allowedModels: readonly string[]
): AgentSelections {
  return Object.fromEntries(
    Object.entries(previous).map(([role, selection]) => [
      role,
      selection?.provider || !selection?.model || allowedModels.includes(selection.model)
        ? selection
        : { ...selection, model: '' },
    ])
  ) as AgentSelections;
}

function defaultModelForAgent({
  role,
  provider,
  models,
  resolution,
  catalog,
}: {
  role: { id: AgentRole; defaultTier: ModelTier };
  provider: ProviderName | undefined;
  models: Record<ModelTier, string>;
  resolution: ProviderResolution | undefined;
  catalog: ReturnType<typeof useProviderModelOptions>['catalog'];
}): string | undefined {
  if (!provider) return undefined;
  const inheritedAgentModel =
    role.id === 'maintenance' ? resolution?.inheritance.maintenanceModel : undefined;
  const candidates = [
    inheritedAgentModel,
    models[role.defaultTier],
    resolution?.inheritance.models[role.defaultTier],
    catalog?.defaultModels[provider]?.[role.defaultTier],
  ];
  const allowedModels = modelOptionsForProvider(catalog, provider);
  return candidates.find((model) => model && allowedModels.includes(model));
}

function normalizeAgentSelections(
  agents: AgentSelections
): Partial<Record<AgentRole, { provider: ProviderName | null; model: string | null }>> {
  return Object.fromEntries(
    Object.entries(agents).map(([role, selection]) => [
      role,
      {
        provider: selection?.provider || null,
        model: selection?.model || null,
      },
    ])
  ) as Partial<Record<AgentRole, { provider: ProviderName | null; model: string | null }>>;
}

export function filterModelSelectionsForProvider(
  previous: Record<ModelTier, string>,
  allowedModels: readonly string[]
): Record<ModelTier, string> {
  return {
    main: previous.main && !allowedModels.includes(previous.main) ? '' : previous.main,
    chat: previous.chat && !allowedModels.includes(previous.chat) ? '' : previous.chat,
    small: previous.small && !allowedModels.includes(previous.small) ? '' : previous.small,
    nano: previous.nano && !allowedModels.includes(previous.nano) ? '' : previous.nano,
    pageTester:
      previous.pageTester && !allowedModels.includes(previous.pageTester)
        ? ''
        : previous.pageTester,
  };
}

export function OrganizationAiProviderCell({
  org,
  onEdit,
}: {
  org: OrganizationWithStats;
  onEdit: () => void;
}) {
  const resolution = org.providerResolution;
  return (
    <Flex align="center" gap="xs">
      {resolution ? (
        <TooltipTrigger delay={200}>
          <Flex align="center" gap="xs" className="cursor-default">
            <TagBadge
              name={resolution.provider}
              color={PROVIDER_SOURCE_COLOR[resolution.source]}
              hideDot
            />
            <Text size="xs" className="text-muted-foreground">
              {resolution.source}
            </Text>
          </Flex>
          <Tooltip>
            <Text className="whitespace-pre-line">{providerSourceTooltip(resolution)}</Text>
          </Tooltip>
        </TooltipTrigger>
      ) : (
        <Text className="text-muted-foreground">—</Text>
      )}
      <Button
        variant="icon"
        size="sm"
        iconOnly
        tooltip="Edit AI provider & models"
        onPress={onEdit}
      >
        <Robot size={14} />
      </Button>
    </Flex>
  );
}

export function OrganizationAiConfigDialog({
  org,
  onClose,
}: {
  org: OrganizationWithStats | null;
  onClose: () => void;
}) {
  const mutation = useUpdateOrgAiConfigMutation();
  const { toast } = useToast();
  const [provider, setProvider] = React.useState<ProviderName | ''>('');
  const [models, setModels] = React.useState<Record<ModelTier, string>>(EMPTY_MODEL_SELECTIONS);
  const [agents, setAgents] = React.useState<AgentSelections>({});
  const { catalog, catalogQuery, providerOptions } = useProviderModelOptions('', {
    providerDefault: 'Default (global knob)',
    modelDefault: 'Default (global knob)',
  });

  React.useEffect(() => {
    if (org) {
      setProvider(org.aiConfig?.provider ?? '');
      setModels(tierModelSelectionsFromConfig(org.aiConfig?.models));
      setAgents(agentSelectionsFromConfig(org.aiConfig?.agents));
    }
  }, [org?.id]);

  if (!org) {
    return (
      <DialogTrigger isOpen={false} onOpenChange={() => {}}>
        <span />
        <Dialog title="AI configuration">
          <DialogContent>
            <Box />
          </DialogContent>
        </Dialog>
      </DialogTrigger>
    );
  }

  const resolved = org.providerResolution;
  const draftOrgProvider = provider || resolved?.inheritance.provider;
  const availableModels = modelOptionsForProvider(catalogQuery.data, draftOrgProvider);

  const handleSave = () => {
    mutation.mutate(
      { orgId: org.id, provider, models, agents: normalizeAgentSelections(agents) },
      {
        onSuccess: () => {
          toast.success('AI config updated');
          onClose();
        },
        onError: (err) => toast.error(err.message),
      }
    );
  };

  return (
    <DialogTrigger isOpen={!!org} onOpenChange={(open) => !open && onClose()} size="4xl">
      <span />
      <Dialog
        title={`AI provider & models for ${org.name}`}
        description="Choose any model supported by the selected provider for each tier, or inherit the global default."
      >
        <DialogContent>
          <Flex direction="column" gap="md">
            <Select
              label="Provider (lab/company)"
              items={providerOptions}
              selectedKey={provider}
              isDisabled={catalogQuery.isLoading}
              placeholder={catalogQuery.isLoading ? 'Loading…' : 'Choose a provider'}
              onSelectionChange={(key) => {
                const next = String(key);
                if (!providerOptions.some((option) => option.id === next)) return;
                setProvider(next as ProviderName | '');
                if (catalog) {
                  const draftOrgProvider = next || resolved?.inheritance.provider;
                  const allowed = modelOptionsForProvider(catalog, draftOrgProvider);
                  setModels((previous) => filterModelSelectionsForProvider(previous, allowed));
                  setAgents((previous) => filterInheritedAgentModelsForProvider(previous, allowed));
                }
              }}
            />
            {MODEL_TIERS.map((tier) => {
              const inheritedModel = resolved?.inheritance.models[tier];
              const allowedModels = modelOptionsForProvider(catalog, draftOrgProvider);
              const defaultModel =
                inheritedModel && allowedModels.includes(inheritedModel)
                  ? inheritedModel
                  : draftOrgProvider
                    ? catalog?.defaultModels[draftOrgProvider]?.[tier]
                    : undefined;
              return (
                <Select
                  key={tier}
                  label={`${MODEL_TIER_LABEL[tier]} model`}
                  items={[
                    {
                      id: '',
                      label: defaultModel ? `Default (${defaultModel})` : 'Default (global knob)',
                    },
                    ...availableModels.map((model) => ({ id: model, label: model })),
                  ]}
                  selectedKey={models[tier]}
                  placeholder={catalogQuery.isLoading ? 'Loading…' : 'Choose a model'}
                  isDisabled={catalogQuery.isLoading}
                  onSelectionChange={(key) =>
                    setModels((previous) => ({
                      ...previous,
                      [tier]: String(key ?? ''),
                    }))
                  }
                />
              );
            })}
            <Flex direction="column" gap="sm" className="border-t border-border pt-4">
              <Text size="sm" weight="medium">
                Core agents
              </Text>
              <Text size="xs" className="text-muted-foreground">
                Choose an independent provider and model for each core agent, or inherit its
                default.
              </Text>
              <Flex direction="column" gap="sm" className="pt-1">
                <Box className={`${AGENT_ROW_GRID} border-b border-border pb-2`}>
                  <Text size="xs" weight="medium" className="text-muted-foreground">
                    Agent
                  </Text>
                  <Text size="xs" weight="medium" className="text-muted-foreground">
                    Provider
                  </Text>
                  <Text size="xs" weight="medium" className="text-muted-foreground">
                    Model
                  </Text>
                </Box>
                {(catalog?.agentRoles ?? []).map((agentRole) => {
                  const role = agentRole.id;
                  const selection = agents[role] ?? EMPTY_AGENT_SELECTION;
                  const agentModels = modelOptionsForProvider(
                    catalog,
                    selection.provider || draftOrgProvider
                  );
                  const defaultModel = defaultModelForAgent({
                    role: agentRole,
                    provider: selection.provider || draftOrgProvider,
                    models,
                    resolution: resolved,
                    catalog,
                  });
                  const agentProviderOptions = [
                    {
                      id: '',
                      label: draftOrgProvider
                        ? `Default (${draftOrgProvider})`
                        : 'Default (global knob)',
                    },
                    ...providerOptions.filter((option) => option.id !== ''),
                  ];
                  return (
                    <Box key={role} className={AGENT_ROW_GRID}>
                      <Text size="sm" weight="medium">
                        {agentRole.label}
                      </Text>
                      <Select
                        aria-label={`${agentRole.label} provider`}
                        className="min-w-0"
                        items={agentProviderOptions}
                        selectedKey={selection.provider}
                        isDisabled={catalogQuery.isLoading}
                        placeholder={catalogQuery.isLoading ? 'Loading…' : 'Choose a provider'}
                        onSelectionChange={(key) => {
                          const nextProvider = String(key ?? '') as ProviderName | '';
                          if (!agentProviderOptions.some((option) => option.id === nextProvider)) {
                            return;
                          }
                          const allowed = modelOptionsForProvider(
                            catalog,
                            nextProvider || draftOrgProvider
                          );
                          setAgents((previous) => ({
                            ...previous,
                            [role]: filterAgentModelSelectionForProvider(
                              {
                                ...EMPTY_AGENT_SELECTION,
                                ...previous[role],
                                provider: nextProvider,
                              },
                              allowed
                            ),
                          }));
                        }}
                      />
                      <Select
                        aria-label={`${agentRole.label} model`}
                        className="min-w-0"
                        items={[
                          {
                            id: '',
                            label: defaultModel
                              ? `Default (${defaultModel})`
                              : 'Default (agent default)',
                          },
                          ...agentModels.map((model) => ({ id: model, label: model })),
                        ]}
                        selectedKey={selection.model}
                        placeholder={catalogQuery.isLoading ? 'Loading…' : 'Choose a model'}
                        isDisabled={catalogQuery.isLoading}
                        onSelectionChange={(key) =>
                          setAgents((previous) => ({
                            ...previous,
                            [role]: {
                              ...EMPTY_AGENT_SELECTION,
                              ...previous[role],
                              model: String(key ?? ''),
                            },
                          }))
                        }
                      />
                    </Box>
                  );
                })}
              </Flex>
            </Flex>
            {mutation.isError && (
              <Flex align="center" gap="sm" className="p-3 rounded-md bg-destructive/10">
                <Warning size={20} className="text-destructive" />
                <Text size="sm" className="text-destructive">
                  {mutation.error?.message ?? 'Failed to update AI config'}
                </Text>
              </Flex>
            )}
          </Flex>
        </DialogContent>
        <DialogFooter>
          <Button variant="ghost" onPress={onClose} isDisabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onPress={handleSave} isDisabled={mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
